import { useCallback } from 'react';
import { useMutation } from 'react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import useAmplitude from 'providers/root/AmplitudeProvider/useAmplitude';
import type { BlockDataRequestDto } from '@reports/application/dtos';
import type { ExportConfig } from '@reports/domain/value-objects';
import { getExportUseCases } from '@reports/composition';
import { generateFilename } from '@reports/presentation/utils/exportUtils';
import { useReportsState } from '@reports/presentation/contexts/useReportsState';
import { buildReportExportAnalytics } from '@reports/presentation/utils';

/**
 * Hook for exporting reports using React Query with Sonner toasts
 *
 * Uses useMutation to handle export state and automatically triggers file download.
 * Shows success/error toasts using Sonner.
 * Gets export use case from composition root (no props needed).
 *
 * @returns Export mutation with state and functions
 *
 * @example
 * const { exportReport, isLoading } = useReportExport();
 *
 * const handleExport = () => {
 *   exportReport(filters, exportConfig);
 * };
 */
export const useReportExport = () => {
  const { t } = useTranslation(['reportCommon']);
  const { state } = useReportsState();
  const { logEvent } = useAmplitude();
  const { exportReport } = getExportUseCases();

  /**
   * Downloads a blob as a file
   *
   * @param blob - File blob to download
   * @param filename - Name of the downloaded file
   */
  const downloadBlob = useCallback((blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  /**
   * React Query mutation for exporting reports
   */
  const mutation = useMutation({
    mutationFn: async (params: { request: BlockDataRequestDto; exportConfig: ExportConfig; startTime: number }) => {
      const { request, exportConfig, startTime } = params;
      const blob = await exportReport.execute(request, exportConfig);
      const loadTimeMs = Date.now() - startTime;
      return { blob, request, exportConfig, loadTimeMs };
    },
    onSuccess: ({ blob, request, exportConfig, loadTimeMs }) => {
      const filename = generateFilename(exportConfig, request, t);
      downloadBlob(blob, filename);

      // Log export analytics
      const reportName = state.currentReport?.type || 'unknown';
      const analyticsData = buildReportExportAnalytics(request, reportName, loadTimeMs);
      logEvent('@event.$reports.reportExported', `@@section.${state.currentReport?.type}`, analyticsData);

      toast.success(t('reportCommon:toasts.exportSuccess.title'), {
        description: t('reportCommon:toasts.exportSuccess.description'),
      });
    },
    onError: (_error: Error) => {
      toast.error(t('reportCommon:toasts.exportError.title'), {
        description: t('reportCommon:toasts.exportError.description'),
      });
    },
  });

  /**
   * Exports a report
   *
   * @param request - Filters and date range for the export
   * @param exportConfig - Export configuration from report definition
   */
  const exportReportAction = useCallback(
    (request: BlockDataRequestDto, exportConfig: ExportConfig) => {
      const startTime = Date.now();
      mutation.mutate({ request, exportConfig, startTime });
    },
    [mutation],
  );

  return {
    exportReport: exportReportAction,
    isLoading: mutation.isLoading,
  };
};
